import React, { useRef, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import { DropdownIndicator } from 'components/application/CollectionSelect';

const getNextPageParam = (lastPage, allPages) => {
  const { currentPage, totalPages } = lastPage.meta;

  return currentPage !== totalPages ? allPages.length + 1 : undefined;
};

export default function SelectTeams({ currentPersonnel, onTeamSelect }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [isDebouncing, setIsDebouncing] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [valueMenuOpen, setValueMenuOpen] = useState(false);

  const assignableTeamsParams = {
    filter: { ids: { nin: currentPersonnel.teamSelections.map((t) => t.id) } },
    search: searchText,
    company_id: currentPersonnel.company.companyId,
  };

  const fetchAssignableTeams = (page) =>
    axios.post('/assignable_team_searches', {
      assignable_team_search: {
        ...assignableTeamsParams,
        page,
      },
    });

  const {
    data: assignableTeams,
    fetchNextPage: fetchNextAssignableTeamsPage,
    hasNextPage: assignableTeamsHasNextPage,
    isFetching: assignableTeamsIsFetching,
  } = useInfiniteQuery({
    queryKey: ['assignableTeams', assignableTeamsParams],
    queryFn: async ({ pageParam = 1 }) => {
      const assignableTeamsResponse = await fetchAssignableTeams(pageParam);
      return assignableTeamsResponse.data;
    },
    getNextPageParam,
    enabled: valueMenuOpen && currentPersonnel?.companyId,
  });

  const handleMenuOpen = () => setValueMenuOpen(true);
  const handleMenuClose = () => setValueMenuOpen(false);

  const handleDebounce = (searchText) => {
    setSearchText(searchText);
    setIsDebouncing(false);
  };
  const debounceInput = useRef(
    _.debounce((searchText) => handleDebounce(searchText), 500),
  ).current;

  const setSearchTextDebounced = (searchText) => {
    if (searchText && searchText.length > 0) {
      setIsDebouncing(true);
    }
    debounceInput(searchText);
  };

  const assignableOptions = (resourceData) => {
    const fullAssignableTeams = resourceData.pages
      .map((page) => page.data)
      .flat();
    const teamsList = fullAssignableTeams.map((team) => ({
      value: {
        id: team.id,
        name: team.attributes.name,
      },
      label: team.attributes.name,
    }));
    return teamsList;
  };

  return (
    <>
      <label
        className='collection-select__label tw-font-medium'
        htmlFor='teamIds'
      >
        {i18nTShared('teams')}
      </label>
      <Select
        className='collection-select__select-container tw-mb-4'
        classNamePrefix='collection-select'
        components={{ DropdownIndicator, Option }}
        id='teamIds'
        isLoading={isDebouncing || assignableTeamsIsFetching}
        loadingMessage={() => i18nTShared('loading...')}
        name='teamIds'
        noOptionsMessage={() =>
          searchText ? i18nTShared('noMatchingResults') : null
        }
        onChange={(e) => onTeamSelect(e.value)}
        onInputChange={setSearchTextDebounced}
        onMenuClose={handleMenuClose}
        onMenuOpen={handleMenuOpen}
        onMenuScrollToBottom={() => {
          if (assignableTeamsHasNextPage) fetchNextAssignableTeamsPage();
        }}
        openMenuOnFocus={true}
        options={
          assignableTeams && !isDebouncing ?
            assignableOptions(assignableTeams)
          : []
        }
        placeholder={i18nTShared('search...')}
        value={null}
      />
    </>
  );
}

export function Option(props) {
  return (
    <components.Option {...props}>
      <div className='collection-select__option_container'>
        <span>{props.children}</span>
      </div>
    </components.Option>
  );
}

SelectTeams.propTypes = {
  currentPersonnel: PropTypes.object,
  onTeamSelect: PropTypes.func.isRequired,
};
